import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Baby, Check, X, Sparkles, ShieldPlus, Scale } from "lucide-react";
import { API, imgUrl } from "@/config";
import { computeRentalPrice } from "@/utils/pricing";
import { disneyApproved } from "@/utils/strollerAttrs";

export const ProductDetailsModal = ({ product, range, onClose, onAdd, selectedQty, scrollTo, initialOptionIds = [], onToggleCompare, comparing = false }) => {
  const cancelSectionRef = useRef(null);
  // Live per-option availability for the requested date range. Sold-out
  // options with a finite cap are hidden from the picker so customers can't
  // pick something we don't have.
  const [optAvail, setOptAvail] = useState(null);
  const [optIds, setOptIds] = useState(initialOptionIds || []);

  useEffect(() => {
    if (scrollTo === "cancellation" && cancelSectionRef.current) {
      const t = setTimeout(() => cancelSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
      return () => clearTimeout(t);
    }
  }, [scrollTo]);

  // Fetch live per-option availability whenever this product opens with a
  // real date range attached. No dates → skip the network call.
  const hasOptionGroups = (product?.option_groups?.length || 0) > 0;
  useEffect(() => {
    if (!product?.id || !range?.from || !range?.to) return;
    if (!hasOptionGroups) return;
    const start = range.from.toISOString().slice(0, 10);
    const end = range.to.toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/products/${product.id}/options-availability`, { params: { start_date: start, end_date: end } });
        if (!cancelled) setOptAvail(r.data);
      } catch {
        if (!cancelled) setOptAvail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [product?.id, hasOptionGroups, range?.from, range?.to]);

  const visibleGroups = useMemo(() => {
    const src = optAvail?.option_groups || product?.option_groups || [];
    return src.map((g) => ({
      ...g,
      options: (g.options || []).filter((o) => o.available === null || o.available === undefined || o.available > 0),
    })).filter((g) => (g.options || []).length > 0);
  }, [optAvail, product]);

  // Pre-select the first available option in each REQUIRED group so the
  // customer never lands in a "must pick" state without a default.
  useEffect(() => {
    if (!visibleGroups.length) return;
    setOptIds((prev) => {
      const next = [...prev];
      for (const g of visibleGroups) {
        const hasPick = (g.options || []).some((o) => next.includes(o.id));
        if (!hasPick && g.required && g.options?.[0]) next.push(g.options[0].id);
      }
      return next;
    });
  }, [visibleGroups]);

  const totalDelta = useMemo(() => {
    let sum = 0;
    for (const g of visibleGroups) {
      for (const o of (g.options || [])) {
        if (optIds.includes(o.id)) sum += Number(o.price_delta_per_day || 0);
      }
    }
    return sum;
  }, [visibleGroups, optIds]);

  const missingRequired = visibleGroups.some((g) => {
    if (!g.required) return false;
    return !(g.options || []).some((o) => optIds.includes(o.id));
  });

  const pickOption = (groupId, optionId) => {
    setOptIds((prev) => {
      // Enforce mutual exclusivity per group — remove any prior pick from
      // this group, then set the new one.
      const group = visibleGroups.find((g) => g.id === groupId);
      const groupOptIds = new Set((group?.options || []).map((o) => o.id));
      const cleaned = prev.filter((id) => !groupOptIds.has(id));
      return [...cleaned, optionId];
    });
  };
  const clearGroup = (groupId) => {
    setOptIds((prev) => {
      const group = visibleGroups.find((g) => g.id === groupId);
      const groupOptIds = new Set((group?.options || []).map((o) => o.id));
      return prev.filter((id) => !groupOptIds.has(id));
    });
  };

  if (!product) return null;
  const p = product;
  const pricing = computeRentalPrice(p, range);
  const freeAccessories = p.free_accessories || [];
  return createPortal(
    <div
      className="fixed inset-0 z-[110] bg-[#040A0F]/90 backdrop-blur-md flex items-center justify-center p-3 md:p-4"
      onClick={onClose}
      data-testid="product-details-modal"
    >
      <div
        className="w-full max-w-3xl relative rounded-2xl border border-[#FFD166]/30 bg-[#0A1A26] shadow-[0_25px_80px_-10px_rgba(0,0,0,0.9)] overflow-hidden max-h-[calc(100dvh-1.5rem)] md:max-h-[calc(100dvh-2rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 z-20 text-[#F6F1E4] hover:text-[#FFD166] p-2 rounded-full bg-black/40 backdrop-blur-md" data-testid="product-details-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="overflow-y-auto overscroll-contain" data-testid="product-details-scroll">
        <div className="grid md:grid-cols-2">
          {/* Image column */}
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px] bg-[#06121B]">
            <img src={imgUrl(p.image)} alt={p.name} className="absolute inset-0 w-full h-full object-contain p-4" />
            <span className="absolute top-4 left-4 eyebrow bg-[#040A0F]/85 px-3 py-1 rounded-full border border-[#FFD166]/25">
              {p.subcategory || p.category}
            </span>
          </div>
          {/* Details column */}
          <div className="p-6 md:p-8 flex flex-col">
            <h2 className="font-serif text-3xl md:text-4xl text-[#F6F1E4] leading-tight" data-testid="product-details-name">{p.name}</h2>
            {p.tagline && <p className="text-[#B9C8D5] italic mt-2">{p.tagline}</p>}
            {p.min_age && (
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#B9C8D5]">
                <Baby className="w-3.5 h-3.5 text-[#FFD166]" /> {p.min_age}
              </div>
            )}
            {disneyApproved(p.stroller_attributes) === true && (
              <div className="mt-2" data-testid="disney-approved-badge">
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300">
                  Disney park approved ✓ <span className="text-emerald-300/70">fits the 31″×52″ limit</span>
                </span>
              </div>
            )}

            {p.description && (
              <p className="mt-4 text-sm text-[#F6F1E4]/90 leading-relaxed" data-testid="product-details-description">
                {p.description}
              </p>
            )}

            {(p.features || []).length > 0 && (
              <ul className="mt-4 space-y-1.5" data-testid="product-details-features">
                {p.features.map((f) => (
                  <li key={f} className="text-sm text-[#F6F1E4]/90 flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-[#FFD166] mt-0.5 shrink-0" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Free accessories always included with the rental */}
            {freeAccessories.length > 0 && (
              <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="product-details-free-accessories">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Included with your rental ✨</div>
                <div className="text-sm text-[#F6F1E4]/90">
                  {freeAccessories.join(" · ")}
                </div>
              </div>
            )}

            {/* Variation groups — customer must pick if group is required */}
            {visibleGroups.length > 0 && (
              <div className="mt-4 space-y-3" data-testid="product-details-variations">
                {visibleGroups.map((g) => {
                  const currentPick = (g.options || []).find((o) => optIds.includes(o.id))?.id || "";
                  return (
                    <div key={g.id} className="rounded-xl border border-[#FFD166]/25 bg-black/30 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#FFD166]">
                          {g.label} {g.required && <span className="text-red-300 normal-case tracking-normal text-[10px]">· required</span>}
                        </div>
                        {!g.required && currentPick && (
                          <button type="button" onClick={() => clearGroup(g.id)} className="text-[10px] text-[#B9C8D5] hover:text-[#FFD166]">Clear</button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {g.options.map((o) => {
                          const active = optIds.includes(o.id);
                          const cap = o.inventory;
                          const remaining = o.available;
                          return (
                            <button
                              key={o.id}
                              type="button"
                              onClick={() => pickOption(g.id, o.id)}
                              className={`text-left rounded-lg border p-2.5 transition-colors ${
                                active
                                  ? "border-[#FFD166] bg-[#FFD166]/10"
                                  : "border-[#FFD166]/20 hover:border-[#FFD166]/50"
                              }`}
                              data-testid={`variation-${g.id}-${o.id}`}
                            >
                              <div className="flex items-center gap-2.5">
                                {o.image && <img src={o.image} alt={o.name} className="w-10 h-10 rounded object-cover shrink-0" />}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-[#F6F1E4] truncate">{o.name}</div>
                                  <div className="text-[11px] text-[#B9C8D5]">
                                    {Number(o.price_delta_per_day) > 0
                                      ? `+$${Number(o.price_delta_per_day).toFixed(2)}/day`
                                      : "Included"}
                                    {((cap !== null && cap !== undefined) || (remaining !== null && remaining !== undefined)) && (
                                      <span className="ml-2 text-[#FFD166]">· {remaining ?? cap} left</span>
                                    )}
                                  </div>
                                </div>
                                {active && <Check className="w-4 h-4 text-[#FFD166] shrink-0" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tier pricing */}
            <div className="mt-6 rounded-xl border border-[#FFD166]/25 bg-black/30 p-4 grid grid-cols-3 gap-2 text-center" data-testid="product-details-tier-pricing">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">1-3 days</div>
                <div className="font-serif text-xl text-[#FFD166]">${(p.tier_1_3 ?? p.price_per_day).toFixed(0)}<span className="text-[10px] text-[#B9C8D5]">/day</span></div>
              </div>
              <div className="border-x border-[#FFD166]/15">
                <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">4-7 days</div>
                <div className="font-serif text-xl text-[#FFD166]">${(p.tier_4_7 ?? p.price_per_day).toFixed(0)}<span className="text-[10px] text-[#B9C8D5]">/day</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">8+ days</div>
                <div className="font-serif text-xl text-[#FFD166]">${(p.tier_8_plus ?? p.price_per_day).toFixed(0)}<span className="text-[10px] text-[#B9C8D5]">/day</span></div>
              </div>
            </div>
            <p className="mt-2 text-[10px] uppercase tracking-wider text-[#F4A93A] text-center">3-day minimum applies</p>

            {/* Your total (if dates picked) */}
            {pricing && (
              <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center justify-between" data-testid="product-details-total">
                <div className="text-xs leading-tight">
                  <div className="uppercase tracking-wider text-emerald-300/85">Your total · 1 unit</div>
                  <div className="text-[#B9C8D5]">
                    {pricing.billableDays} day{pricing.billableDays === 1 ? "" : "s"}
                    {pricing.isMinApplied && <span className="text-[#F4A93A]"> (3-day min)</span>}
                    {" · "}${pricing.rate.toFixed(0)}/day
                  </div>
                </div>
                <div className="font-serif text-2xl text-emerald-300 leading-none">${pricing.total.toFixed(0)}</div>
              </div>
            )}

            {/* Cancellation — Enchanted Peace of Mind + Care preview so the
                "Cancel anytime ✨" ribbon on the product card has a payoff.
                Kept concise; the checkout modal shows full policy math. */}
            <div
              ref={cancelSectionRef}
              className="mt-5 rounded-xl border border-[#FFD166]/30 bg-[#FFD166]/5 p-4"
              data-testid="product-details-cancellation"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#FFD166]" />
                <div className="font-serif text-lg text-[#FFD166]">How cancellation works</div>
              </div>
              <div className="space-y-2 text-[13px] text-[#F6F1E4]/90 leading-snug">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#FFD166]" aria-hidden>✨</span>
                  <div>
                    <b className="text-[#FFD166]">Enchanted Peace of Mind (20 %)</b> — cancel any time before delivery for a 100 % rental refund. The 20 % PoM fee itself and credit-card processing fees remain non-refundable.
                    <span className="block text-[11px] text-[#B9C8D5] mt-0.5">Only offered when you book at least 7 days ahead of delivery.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldPlus className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" />
                  <div>
                    <b className="text-emerald-300">Enchanted Care (8 %, $5 min)</b> — covers minor accidental damage during your rental. Refunded along with your booking if you cancel before delivery; kept once the trip actually starts.
                  </div>
                </div>
                <div className="text-[11px] text-[#B9C8D5] pt-2 border-t border-[#FFD166]/15">
                  <b className="text-[#F6F1E4]">Standard cancellation:</b> 15+ days notice = 100 % refund (minus credit-card processing fees); 7-14 days = 50 %; less than 7 days = non-refundable. Once your reservation starts, no refunds — rescheduling only. Driver tips are fully refundable before delivery day.
                </div>
              </div>
            </div>

            {p.stroller_attributes?.include_in_comparison && onToggleCompare && (
              <button
                type="button"
                onClick={() => { onToggleCompare(p); onClose(); }}
                className={`mt-5 w-full inline-flex items-center justify-center gap-2 text-xs rounded-full border px-4 py-2.5 transition-all ${
                  comparing ? "bg-[#F4A93A] text-[#040A0F] border-[#F4A93A]" : "border-[#F4A93A]/45 text-[#F4A93A] hover:bg-[#F4A93A]/10"
                }`}
                data-testid="product-details-compare"
              >
                <Scale className="w-3.5 h-3.5" /> {comparing ? "Remove from comparison" : "Compare this stroller"}
              </button>
            )}

            <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={onClose} className="btn-ghost sm:flex-1" data-testid="product-details-close-btn">
                Close
              </button>
              <button
                onClick={() => { onAdd?.(p.id, { option_ids: optIds }); onClose(); }}
                disabled={missingRequired}
                className={`sm:flex-1 text-sm font-medium px-5 py-2.5 rounded-full border transition-all ${
                  missingRequired
                    ? "bg-black/30 border-[#FFD166]/30 text-[#B9C8D5] cursor-not-allowed"
                    : "bg-[#FFD166] text-[#040A0F] border-[#FFD166] hover:brightness-110"
                }`}
                data-testid="product-details-add"
              >
                {missingRequired
                  ? "Please pick an option above"
                  : selectedQty
                    ? `× ${selectedQty} in cart — Add another`
                    : `Add to booking${totalDelta > 0 ? ` (+$${totalDelta.toFixed(2)}/day)` : ""}`}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
